import type { PrinterCapabilities } from './types';

function detectPlatform(): PrinterCapabilities['platform'] {
  if (typeof navigator === 'undefined') return 'unknown';
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/i.test(ua)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  if (/Win/i.test(ua)) return 'windows';
  if (/Mac/i.test(ua)) return 'macos';
  if (/Linux/i.test(ua)) return 'linux';
  return 'unknown';
}

/** Chrome, Edge, Opera, and other Chromium-based browsers (Web Bluetooth target). */
export function isChromiumBrowser(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/Edg\//.test(ua) || /OPR\//.test(ua) || /Chrome\//.test(ua)) {
    return !/Brave/i.test(ua);
  }
  return false;
}

/**
 * Web Bluetooth / Web Serial availability. iOS Safari does not expose practical printer APIs.
 */
export function getPrinterCapabilities(): PrinterCapabilities {
  const platform = detectPlatform();
  const secureContext =
    typeof globalThis !== 'undefined' && 'isSecureContext' in globalThis
      ? Boolean((globalThis as { isSecureContext?: boolean }).isSecureContext)
      : typeof window !== 'undefined' && window.isSecureContext === true;

  const chromium = isChromiumBrowser();
  const webBluetooth =
    secureContext && typeof navigator !== 'undefined' && 'bluetooth' in navigator && Boolean(navigator.bluetooth);
  const webSerial =
    secureContext && typeof navigator !== 'undefined' && 'serial' in navigator && Boolean(navigator.serial);

  let unsupportedReason: string | undefined;
  let canPrint = secureContext && (webBluetooth || webSerial);

  if (platform === 'ios') {
    canPrint = false;
    unsupportedReason = 'bluetoothPrinter.platform.iosNotSupported';
  } else if (!secureContext) {
    canPrint = false;
    unsupportedReason = 'bluetoothPrinter.platform.needHttps';
  } else if (!webBluetooth && !webSerial) {
    canPrint = false;
    unsupportedReason = 'bluetoothPrinter.platform.noWebApis';
  }

  const requiresChromium = Boolean(webBluetooth);

  return {
    secureContext,
    webBluetooth,
    webSerial,
    canPrint,
    platform,
    unsupportedReason,
    isChromium: chromium,
    requiresChromium,
  };
}

export function getPlatform(): PrinterCapabilities['platform'] {
  return detectPlatform();
}
